import { FC, useState, useEffect, useMemo } from 'react'
import {
  cn,
  useIsMobile,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@wellesley/ui'
import { MediaItem } from '../MediaItem'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { isUploadReady, mapUpload } from '@wellesley/uploads'
import useBodyWidth from 'hooks/useBodyWidth'
import useGalleryState from './hooks/useGalleryState'
import { PREVIEW_CONTROL } from 'constants/ui'
import { useKeyboardNavigation } from 'hooks/slider/useKeyboardNavigation'
import CommonDialog from 'components/modals/CommonDialog'

import { OpenGalleryProps, MediaGalleryProps } from './types'

const commonMediaClass = `max-h-[80vh] w-full grow-1 mx-auto my-0 border-0`

const MediaGallery: FC<MediaGalleryProps> = ({
  uploads: allUploads,
  allowWithoutSize,
  children,
}) => {
  const uploads = useMemo(() => {
    return allUploads
      .filter((u) => isUploadReady(u, allowWithoutSize))
      .map(mapUpload)
  }, [allUploads, allowWithoutSize])

  const [startIndex, setStartIndex] = useState<number>()
  const [api, setApi] = useState<CarouselApi>()
  const [currentSlide, setCurrentSlide] = useState(0)

  const isMobile = useIsMobile()
  const bodyWidth = useBodyWidth()
  const [isGalleryOpened, openGallery, closeGallery] = useGalleryState()

  useKeyboardNavigation({ api, canNavigate: isGalleryOpened })

  useEffect(() => {
    if (!api) return

    setCurrentSlide(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrentSlide(api.selectedScrollSnap() + 1)
    })
  }, [api])

  const openGalleryWithId = ({ id, preview }: OpenGalleryProps) => {
    if (!bodyWidth) return

    let slideIndex = 0

    if (id) {
      slideIndex = Math.max(
        0,
        uploads.findIndex((u) => u.uploadId === id),
      )
    } else if (preview) {
      slideIndex = uploads.findIndex((u) => u.files[0].uri === preview)
    }

    setStartIndex(slideIndex)
    openGallery()
  }

  const totalSlides = uploads.length

  return (
    <>
      <CommonDialog
        open={isGalleryOpened}
        onCloseModal={closeGallery}
        id="media_gallery"
        className={cn(
          'flex h-full max-w-[100vw] items-center justify-center rounded-none border-none bg-transparent p-0',
          isMobile && 'bg-gray-900 max-md:w-full max-md:max-w-full',
        )}
        childrenClassName="!overflow-visible !max-h-[100vh]"
        closeVariant={isMobile ? 'mobileGallery' : 'outsideCard'}
        closeSize={isMobile ? '18x18' : 'big'}
        showClose={false}
        modal
      >
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            startIndex,
          }}
          className="w-[100vw]"
        >
          <CarouselContent className="items-center gap-5">
            {uploads.map((upload, index) => (
              <CarouselItem
                data-testid="carouselItem"
                key={`${upload.uploadId}/${upload.meta?.name}${index}`}
                isFullPreview={true}
                className={cn(
                  'flex h-full w-full max-w-full justify-center',
                  isMobile && 'pr-0',
                )}
              >
                <MediaItem
                  isTooltip={false}
                  allowWithoutSize={allowWithoutSize}
                  upload={upload}
                  imgClass={commonMediaClass}
                  videoClass={commonMediaClass}
                  canPlayInModal={true}
                  containerWidth={bodyWidth}
                  inView={totalSlides > 1 ? currentSlide === index + 1 : true}
                  onClosePreview={closeGallery}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {totalSlides > 1 && (
          <div
            data-id={PREVIEW_CONTROL}
            className="[&_div]:bg-alpha-black-30 text-white [&_div]:fixed [&_div]:top-1/2 [&_div]:z-10 [&_div]:flex [&_div]:size-12 [&_div]:translate-y-[-50%] [&_div]:cursor-pointer [&_div]:items-center [&_div]:justify-center [&_div]:rounded-[50%] [&_div]:backdrop-blur-sm"
          >
            {currentSlide > 1 && (
              <div className="left-10" onClick={() => api?.scrollPrev()}>
                <ChevronLeft size={24} />
              </div>
            )}
            {currentSlide < totalSlides && (
              <div className="right-10" onClick={() => api?.scrollNext()}>
                <ChevronRight size={24} />
              </div>
            )}
          </div>
        )}
      </CommonDialog>
      {children(openGalleryWithId)}
    </>
  )
}

export default MediaGallery
